import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import clock from '~/assets/icons/clock.svg';
import crossRound from '~/assets/icons/cross_round.svg';
import info from '~/assets/icons/info_2.svg';
import play from '~/assets/icons/play.svg';
import task from '~/assets/icons/task.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';

import { rage, useInputAction, useObservable } from '~/rage';
import { Currency, formatTime, InputAction, numWord } from '~/shared';
import previewTaxi2 from '../../assets/images/preview_taxi_2.jpg';
import previewTaxi3 from '../../assets/images/preview_taxi_3.jpg';
import { usePlayerLevel } from '../../hooks';
import { setActiveStage, useLayoutDispatch } from '../../redux';
import { JobDefinitions } from '../../views/Jobs/Jobs.model';
import { JobsStage } from '../JobsPanel/JobsPanel.model';
import { Task } from '../Task';
import {
	StyledButtonContent,
	StyledButtonsGrid,
	StyledClock,
	StyledContainer,
	StyledDescContainer,
	StyledEarningsContainer,
	StyledGallaryContainer,
	StyledGallaryImage,
	StyledProgessContainer,
	StyledProgress,
	StyledProgressIndicator,
	StyledProgressWrapper,
	StyledProgressXPContainer,
	StyledPromoContainer,
	StyledSliderContainer,
	StyledSliderDot,
	StyledTasksContainer,
	StyledTasksGrid,
	StyledTasksHead,
	StyledTasksTimer,
	StyledTopContainer,
} from './Main.styled';

const Main = () => {
	const dispatch = useLayoutDispatch();
	const jobId = useObservable(rage.jobs.getOpenedJobId());
	const jobConfig = useMemo(
		() => (jobId ? JobDefinitions[jobId] : null),
		[jobId],
	);
	const taskList = useObservable(rage.jobs.getTasks());
	const playerAtWork = useObservable(rage.jobs.getPlayerAtWork());
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const withRentVehicles = useObservable(rage.jobs.getWithRentVehicles());
	const withOwnedVehicles = useObservable(rage.jobs.getWithOwnedVehicles());
	const {
		exp: unitProgressCount,
		maxExp: nextLevelUnitCount,
		playerLevel,
		nextLevel,
		playerMaxLevel,
	} = usePlayerLevel();
	const promotions = useObservable(rage.jobs.getJobPromotions());
	const [currentSlide, setCurrentSlide] = useState(0);
	const promotionTimerRef = useRef<NodeJS.Timeout | null>(null);
	const promotionTimeRef = useRef<HTMLDivElement>(null);
	const indicatorRefs = useRef<HTMLDivElement[]>([]);
	const progressRef = useRef(0);
	const taskExpire = useObservable(rage.jobs.getTasksExpireAt());
	const [taskExpireSeconds, setTaskExpireSeconds] = useState(0);

	const [dailyProfit, setDailyProfit] = useState(0);

	useEffect(() => {
		if (!jobId) {
			return;
		}

		rage.bank
			.getDailyJobProfit(jobId)
			.then(setDailyProfit)
			.catch((err) => rage.notifications.showException(err));
	}, [jobId]);

	const promotion = useMemo(() => {
		return promotions.find((promotion) => promotion.jobId === jobId);
	}, [promotions, jobId]);

	const promotionActive = useMemo(() => {
		if (!promotion) {
			return false;
		}
		return promotion.promotionEndsAt > new Date().getTime();
	}, [promotion]);

	const timeString = useMemo(
		() =>
			taskExpireSeconds > 0
				? formatTime(taskExpireSeconds * 1000, '{hh}:{mm}')
				: '00:00',
		[taskExpireSeconds],
	);

	const slides = [
		{ image: jobConfig?.headerImage },
		{ image: previewTaxi2 },
		{ image: previewTaxi3 },
	];

	const handleSlideChange = (index: number) => {
		for (const el of indicatorRefs.current) {
			if (el) {
				el.style.transform = 'scaleX(0)';
			}
		}
		progressRef.current = 0;
		setCurrentSlide(index);
	};

	const startOrStopShift = useCallback(() => {
		if (playerAtWork) {
			rage.jobs
				.stopWorkShift()
				.catch((err) => rage.notifications.showException(err));
			return;
		}

		if (withRentVehicles || withOwnedVehicles) {
			dispatch(setActiveStage(JobsStage.SELECT_VEHICLE));
			return;
		}

		rage.jobs
			.startWorkShift()
			.catch((err) => rage.notifications.showException(err));
	}, [playerAtWork, withRentVehicles, withOwnedVehicles]);

	useInputAction(InputAction.CommonMenuAccept, startOrStopShift);

	useEffect(() => {
		for (const el of indicatorRefs.current) {
			if (el) {
				el.style.transform = 'scaleX(0)';
			}
		}
		progressRef.current = 0;

		const progressInterval = setInterval(() => {
			const el = indicatorRefs.current[currentSlide];
			if (!el) {
				return;
			}

			if (progressRef.current >= 1) {
				el.style.transform = 'scaleX(1)';
				setCurrentSlide((current) => (current + 1) % slides.length);
				progressRef.current = 0;
				return;
			}

			progressRef.current = Math.min(1, progressRef.current + 0.02);
			el.style.transform = `scaleX(${progressRef.current})`;
		}, 100);

		return () => clearInterval(progressInterval);
	}, [currentSlide, slides.length]);

	useEffect(() => {
		const targetTime = new Date(taskExpire);

		const updateTimeDifference = () => {
			const now = new Date();
			const diff = Math.floor((targetTime.getTime() - now.getTime()) / 1000);
			setTaskExpireSeconds(diff);
		};

		updateTimeDifference();
		const intervalId = setInterval(updateTimeDifference, 60000);

		return () => clearInterval(intervalId);
	}, [taskExpire]);

	useEffect(() => {
		if (!promotionActive || !promotion) {
			if (promotionTimeRef.current) {
				promotionTimeRef.current.textContent = '';
			}
			if (promotionTimerRef.current) {
				clearInterval(promotionTimerRef.current);
				promotionTimerRef.current = null;
			}
			return;
		}

		const updatePromotionTimer = () => {
			const timeLeft = (promotion?.promotionEndsAt ?? 0) - Date.now();
			if (timeLeft <= 0) {
				return;
			}

			if (promotionTimeRef.current) {
				promotionTimeRef.current.textContent = formatTime(
					Math.max(0, timeLeft),
					'{hh}:{mm}',
				);
			}
		};

		updatePromotionTimer();
		promotionTimerRef.current = setInterval(updatePromotionTimer, 60000);

		return () => {
			if (promotionTimerRef.current) {
				clearInterval(promotionTimerRef.current);
				promotionTimerRef.current = null;
			}
		};
	}, [promotionActive, promotion]);

	if (!jobConfig) {
		return null;
	}

	return (
		<StyledContainer>
			<StyledGallaryContainer>
				<StyledGallaryImage
					$currentSlide={currentSlide}
					style={{
						backgroundImage: `url(${
							slides[currentSlide]?.image || jobConfig.headerImage
						})`,
					}}
				/>
				<StyledTopContainer>
					{promotionActive ? (
						<StyledPromoContainer>
							<Text>X2</Text>
							<div className="dividers">
								<Divider $direction="V" style={{ opacity: 0.25 }} />
								<Divider
									$direction="V"
									$color="#000000"
									style={{ opacity: 0.25 }}
								/>
							</div>
							<img src={clock} />
							<Text ref={promotionTimeRef} />
						</StyledPromoContainer>
					) : (
						<div />
					)}
					<StyledEarningsContainer>
						<Text style={{ opacity: 0.5 }}>Заработок за день</Text>
						<Text style={{ fontWeight: 700 }}>
							{dailyProfit.toLocaleString('ru-RU')}
						</Text>
						<CurrencyIcon icon={rubleIcon} size="sm" />
					</StyledEarningsContainer>
				</StyledTopContainer>
				<StyledSliderContainer>
					{slides.map((_, index) => (
						<StyledSliderDot
							key={index}
							$active={currentSlide === index}
							onClick={() => handleSlideChange(index)}
						>
							<StyledProgressIndicator
								$active={currentSlide === index}
								ref={(el) => {
									if (el) {
										indicatorRefs.current[index] = el;
									}
								}}
							/>
						</StyledSliderDot>
					))}
				</StyledSliderContainer>
				<StyledProgressWrapper>
					<StyledProgessContainer>
						<StyledProgress
							style={{
								width:
									playerLevel === playerMaxLevel
										? '100%'
										: (unitProgressCount / nextLevelUnitCount) * 100 + '%',
								backgroundColor:
									playerLevel === playerMaxLevel ? '#FABE2C' : '#2d70d9',
							}}
						/>
					</StyledProgessContainer>
					<Text
						style={{
							opacity: playerLevel === playerMaxLevel ? 0 : 1,
							zIndex: 2,
						}}
					>
						{playerLevel} уровень
					</Text>
					<StyledProgressXPContainer
						$isMaxLevel={playerLevel === playerMaxLevel}
					>
						<img src={jobConfig.icon} />
						<Text>
							{playerLevel === playerMaxLevel ? (
								<Fragment>У вас максимальный уровень</Fragment>
							) : (
								<Fragment>
									<span>{unitProgressCount}</span> / {nextLevelUnitCount}{' '}
									{numWord(nextLevelUnitCount, jobConfig.wordForms)}
								</Fragment>
							)}
						</Text>
					</StyledProgressXPContainer>
					<Text
						style={{
							fontWeight: 500,
							textAlign: 'right',
							opacity: playerLevel === playerMaxLevel ? 0 : 1,
							zIndex: 2,
						}}
					>
						{nextLevel} уровень
					</Text>
				</StyledProgressWrapper>
			</StyledGallaryContainer>
			<StyledDescContainer>
				<Text>
					{jobConfig.descriptionSummary
						.trim()
						.split('\n')
						.map((str, index) => (
							<Fragment key={`panelDesc#${index}`}>
								{str}
								<br />
							</Fragment>
						))}
				</Text>
			</StyledDescContainer>
			<StyledTasksContainer>
				<StyledTasksHead>
					<SectionHeading icon={task} text="Доступные задачи" />
					<StyledTasksTimer>
						<Text>Задачи обновятся через</Text>
						<StyledClock />
						<Text>{timeString}</Text>
					</StyledTasksTimer>
				</StyledTasksHead>
				<StyledTasksGrid>
					{taskList.slice(0, 2).map((item, index) => (
						<Task
							name={item.name}
							text={item.description}
							exp={item.rewardExp || 0}
							amount={item.rewardMoney || 0}
							progress={(item.currentCount / item.targetCount) * 100}
							key={index}
						/>
					))}
				</StyledTasksGrid>
			</StyledTasksContainer>
			<StyledButtonsGrid>
				<Button
					color={ButtonColors.Help}
					height={ButtonHeight.Height60}
					style={{ fontSize: 16 }}
					onClick={() => dispatch(setActiveStage(JobsStage.ABOUT))}
				>
					<StyledButtonContent>
						<img src={info} style={{ filter: 'invert(1)' }} />
						<Text>Подробнее о работе</Text>
					</StyledButtonContent>
				</Button>
				<Button
					color={ButtonColors.Select}
					height={ButtonHeight.Height60}
					style={{ fontSize: 16 }}
					onClick={startOrStopShift}
				>
					<StyledButtonContent>
						<img src={playerAtWork ? crossRound : play} />
						<Text>{playerAtWork ? 'Закончить смену' : 'Начать работу'}</Text>
					</StyledButtonContent>
				</Button>
			</StyledButtonsGrid>
		</StyledContainer>
	);
};

export { Main };
